import React, { useState } from 'react'
import LoginFrame from '../shared/LoginFrame'
import {
  useAppSelector,
  useAppDispatch,
  selectSignupData,
} from '../../store/store'
import { updateSignupField, nextStepAction } from '../../store'
import { preventDefaultAndValidate } from '../../helpers'
import {
  InjectedFrameProps,
  withFrameProps,
} from '../../context/withFrameProps'

type PureSignupNamePromptProps = {
  initialFirstName: string
  initialLastName: string
  onContinue: (firstName: string, lastName: string) => void
} & InjectedFrameProps

export const PureSignupNamePrompt = ({
  initialFirstName,
  initialLastName,
  onContinue,
  ...props
}: PureSignupNamePromptProps) => {
  const [firstName, setFirstName] = useState(initialFirstName)
  const [lastName, setLastName] = useState(initialLastName)

  const handleSubmit = (e: React.SyntheticEvent<HTMLElement>) => {
    onContinue(firstName, lastName)
  }

  return (
    <LoginFrame
      title={'Create your account'}
      {...props}
      recaptchaSiteKey={null}
    >
      <label className="block text-sm font-medium text-gray-900">
        Your name
      </label>
      <div className="flex flex-rows items-center justify-center gap-2 !mt-3">
        <label htmlFor="first_name" className="sr-only">
          First Name
        </label>
        <input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          type="first_name"
          autoFocus
          tabIndex={1}
          name="first_name"
          id="first_name"
          autoComplete="given-name"
          className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
          placeholder="First Name"
          required
        />
        <label htmlFor="last_name" className="sr-only">
          Last Name
        </label>
        <input
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          type="last_name"
          tabIndex={2}
          name="last_name"
          id="last_name"
          autoComplete="family-name"
          className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
          placeholder="Last Name"
          required
        />
      </div>
      <button
        onClick={preventDefaultAndValidate(handleSubmit)}
        type="submit"
        tabIndex={3}
        className="w-full text-white bg-slate-600 hover:bg-slate-700 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
      >
        Next
      </button>
    </LoginFrame>
  )
}

const BoundSignupNamePrompt = withFrameProps(PureSignupNamePrompt, {
  hasPreviousStep: true,
})

export const SignupNamePrompt = () => {
  const dispatch = useAppDispatch()
  const signupData = useAppSelector(selectSignupData)

  const handleSubmit = (firstName: string, lastName: string) => {
    dispatch(updateSignupField({ field: 'firstName', value: firstName }))
    dispatch(updateSignupField({ field: 'lastName', value: lastName }))
    dispatch(nextStepAction())
  }

  return (
    <BoundSignupNamePrompt
      initialFirstName={signupData?.firstName || ''}
      initialLastName={signupData?.lastName || ''}
      onContinue={handleSubmit}
    />
  )
}

export default SignupNamePrompt
